/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import isNil from 'lodash/isNil';
import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { requestGroundStationCoverage } from '../../../store/groundStations/actions';
import { toggleHaloNetGroundStation } from '../../../store/haloNetGroundStations/actions';
import { GroundStationType } from '../../../shared/entities/haloNet';
import {
  selectHaloNetGroundStations,
  selectGroundStationCoverage,
  selectGroundStationVisibleStations,
} from '../../../store';
import NetworkToggle, { NetworkToggleItem } from './NetworkToggle';

const RTEGroundStationControls = () => {
  const dispatch = useDispatch();

  const groundStations = useSelector(selectHaloNetGroundStations);
  const coverage = useSelector(selectGroundStationCoverage, shallowEqual);
  const visibleStations = useSelector(selectGroundStationVisibleStations, shallowEqual);

  const [expanded, setExpanded] = useState(false);
  const disabled = isNil(groundStations) || groundStations.length === 0;
  const hasRequestedCoverageRef = useRef(false);

  // Request coverage data once on mount if not already loaded
  useEffect(() => {
    if (!isNil(groundStations) && coverage.features.length === 0 && !hasRequestedCoverageRef.current) {
      hasRequestedCoverageRef.current = true;
      dispatch(requestGroundStationCoverage());
    }
  }, [dispatch, groundStations, coverage.features.length]);

  const groundStationItems: NetworkToggleItem[] = useMemo(() => {
    if (isNil(groundStations)) {
      return [];
    }

    return groundStations
      .filter(station => station.type === GroundStationType.GROUND_STATION_TYPE_RTE)
      .map(station => {
        const groundStationName = station.groundStationName;
        return {
          id: groundStationName,
          label: groundStationName,
          checked: !!visibleStations[groundStationName],
          networkType: 'ground-station',
          mapType: 'vector',
        };
      });
  }, [groundStations, visibleStations]);

  const handleCheckboxChange = useCallback(
    (item: NetworkToggleItem, checked: boolean) => {
      const isCurrentlyChecked = !!visibleStations[item.id];

      if (isCurrentlyChecked !== checked) {
        dispatch(toggleHaloNetGroundStation(item.id));
      }
    },
    [dispatch, visibleStations],
  );

  const handleExpansionToggle = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  if (isNil(groundStations)) {
    return null;
  }

  return (
    <NetworkToggle
      id="ground-station"
      title="RTE Ground Sites"
      disabled={disabled}
      expanded={expanded}
      items={groundStationItems}
      handleExpansionButtonClick={handleExpansionToggle}
      handleCheckboxChange={handleCheckboxChange}
    />
  );
};

export default RTEGroundStationControls;
